import { AnimatePresence, motion } from "framer-motion";
import { slideBlocks } from "./components/SlideBlocks";
import { slidesMeta, type PitchSlide } from "./components/SlideData";

const ease = [0.25, 0.46, 0.45, 0.94] as const;

export function SlidesHeader({ onPresent }: { onPresent: () => void }) {
  return (
    <div className="mx-auto mb-6 flex max-w-7xl flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-xl sm:px-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300/70">Pitch Deck</p>
        <h1 className="mt-1 text-xl font-semibold text-white sm:text-2xl">Krato AI Slides</h1>
      </div>
      <div className="flex items-center gap-2">
        <a
          href="/#docs"
          className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm text-white/70 transition hover:bg-white/10 hover:text-white"
        >
          Docs
        </a>
        <button
          onClick={onPresent}
          className="rounded-lg border border-cyan-300/40 bg-gradient-to-r from-cyan-500/20 to-sky-500/20 px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(34,211,238,0.22)] transition hover:from-cyan-500/30 hover:to-sky-500/30"
        >
          Enter Presentation Mode
        </button>
      </div>
    </div>
  );
}

function SlideContent({ slide, active }: { slide: PitchSlide; active: number }) {
  const isCover = slide.layout === "cover";

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={active}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 sm:pr-2"
        initial={{ opacity: 0, y: 28, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.985 }}
        transition={{ duration: 0.38, ease }}
      >
        {!isCover && (
          <motion.h2
            className="max-w-5xl text-[clamp(1.45rem,7vw,4.6rem)] font-semibold leading-[1.03] tracking-[-0.04em] text-white sm:text-[clamp(2rem,5vw,4.6rem)]"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.05, ease }}
          >
            {slide.title}
          </motion.h2>
        )}
        {!isCover && slide.subtitle && (
          <motion.p
            className="mt-4 max-w-3xl text-[clamp(0.98rem,4vw,1.7rem)] leading-relaxed text-white/60 sm:mt-5"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.12, ease }}
          >
            {slide.subtitle}
          </motion.p>
        )}
        <motion.div
          className={isCover ? "mt-2 sm:mt-3" : "mt-5 sm:mt-8"}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.18, ease }}
        >
          {slideBlocks[active]}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function SlideControls({
  active,
  onPrevious,
  onNext,
  onSelect,
  total,
  presentationMode,
}: {
  active: number;
  onPrevious: () => void;
  onNext: () => void;
  onSelect: (index: number) => void;
  total: number;
  presentationMode: boolean;
}) {
  return (
    <div
      className={`mt-3 flex shrink-0 items-center justify-between gap-2 border-t border-white/10 pt-3 sm:mt-6 sm:gap-4 sm:pt-4 ${presentationMode ? "sticky bottom-0 z-20 -mx-3 bg-[#0b1230]/90 px-3 pb-[max(0.6rem,env(safe-area-inset-bottom))] backdrop-blur-lg sm:mx-0 sm:bg-transparent sm:px-0 sm:pb-0" : ""}`}
    >
      <button
        onClick={onPrevious}
        disabled={active === 0}
        className="rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white/85 transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-30 sm:px-4 sm:text-sm"
      >
        Previous
      </button>
      <div className="flex flex-1 items-center gap-1.5">
        {slidesMeta.map((_, i) => (
          <button
            key={i}
            onClick={() => onSelect(i)}
            className={`h-1.5 flex-1 rounded-full transition ${i <= active ? "bg-cyan-300 shadow-[0_0_14px_rgba(34,211,238,0.7)]" : "bg-white/10 hover:bg-white/20"}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
      <button
        onClick={onNext}
        disabled={active === total - 1}
        className="rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white/85 transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-30 sm:px-4 sm:text-sm"
      >
        Next
      </button>
    </div>
  );
}

export function SlideDeck({ active, presentationMode, onPrevious, onNext, onSelect }: {
  active: number;
  presentationMode: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onSelect: (index: number) => void;
}) {
  const slide = slidesMeta[active];

  return (
    <section className={`mx-auto flex min-h-0 ${presentationMode ? "h-[100dvh] max-w-[1500px] rounded-none border-x-0 border-y-0 p-2 shadow-none sm:h-[calc(100dvh-1rem)] sm:rounded-3xl sm:border sm:p-6" : "h-[calc(100dvh-5rem)] max-h-[900px] min-h-[560px] max-w-7xl sm:h-auto sm:min-h-[680px]"} flex-col overflow-hidden border border-white/10 bg-[linear-gradient(145deg,rgba(20,24,52,0.88),rgba(10,12,32,0.92))] shadow-[0_20px_70px_rgba(2,6,23,0.55)] backdrop-blur-xl lg:p-10`}>
      <div className="mb-3 flex shrink-0 items-center justify-between gap-3 sm:mb-6 sm:gap-4">
        <span className="rounded-full border border-cyan-400/25 bg-cyan-400/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-200 sm:px-3 sm:text-xs sm:tracking-[0.18em]">{slide.eyebrow}</span>
        <span className="shrink-0 text-xs text-white/35 sm:text-sm">{active + 1} / {slidesMeta.length}</span>
      </div>
      <SlideContent slide={slide} active={active} />
      <SlideControls
        active={active}
        onPrevious={onPrevious}
        onNext={onNext}
        onSelect={onSelect}
        total={slidesMeta.length}
        presentationMode={presentationMode}
      />
    </section>
  );
}

